import {
	ProductSearchResultState as ProductSearchResultType,
	ProductSearchRefinement,
	RearrangedProductSearchRefinement
} from '../../store/data/productSearchResult/interface';
import ManagerRepository from 'coreClasses/ManagerRepository';
import SearchModelPage from './SearchModelPage';
import RefinementModel from './RefinementModel';
import ProductSearchHitModel from './ProductSearchHitModel';
import {ProductSearchSortingOption} from 'brandComponents/Refinements/IRefinements';
import {
	DEFAULT_SEARCH_PARAMS,
	FILTERS_ARRANGE_OPTIONS,
	REFINEMENT_DISALLOW_LIST,
	CATEGORIES_FILTER_ID,
	SHOULD_REARRANGE_REFINEMENTS,
	REFINEMENTS_VALUE_SEPARATOR
} from 'brand/app/constants';
import {isSfccURL} from 'coreUtils/url';
import RefinementValueModel from './RefinementValueModel';
import AbstractSearchProvider from './providers/AbstractSearchProvider';

/**
 * Represents SearchModel
 */
class SearchModel {
	private managerRepository: ManagerRepository;
	private search: ProductSearchResultType;
	private redirectUrl: string;
	private refinements: Array<RefinementModel>;
	private searchProvider: AbstractSearchProvider;
	private pageSizeLimit?: number;

	/**
	 * @constructor
	 * @param {AbstractSearchProvider} searchProvider - Search manager instance
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductSearchResultType} search - search
	 * @param {number} [pageSizeLimit] - Page size from SearchMgr (not persisted in Redux)
	 */
	constructor(
		searchProvider: AbstractSearchProvider,
		managerRepository: ManagerRepository,
		search: ProductSearchResultType,
		pageSizeLimit?: number,
	) {
		this.searchProvider = searchProvider;
		this.managerRepository = managerRepository;
		this.search = search;
		this.pageSizeLimit = pageSizeLimit;
	}

	/**
	 * Returns the search result
	 * @return {ProductSearchResultType}
	 */
	getSearchResult() {
		return this.search;
	}

	/**
	 * Returns an array of the pages contained in the search result.
	 * @return SearchModelPage[]
	 */
	getPages(): SearchModelPage[] {
		if (!this.search.pages) {
			return [];
		}

		return Object.keys(this.search.pages).map(
			(pageNumber) => new SearchModelPage(
				this.managerRepository,
				parseInt(pageNumber),
				this.search.pages[pageNumber]
			),
		);
	}

	/**
	 * Returns the page with the given number or null if the page doesn't exist
	 * @param {number} number - number
	 * @return {SearchModelPage|null}
	 */
	getPage(number: number): SearchModelPage | null {
		return this.search.pages && number in this.search.pages
			? new SearchModelPage(this.managerRepository, number, this.search.pages[number])
			: null;
	}

	/**
	 * Returns the number of the products loaded till now.
	 * @param {boolean} includePreviousNotLoadedPages - True if it has to count also the product not yet loaded in the previous pages
	 * @return number
	 */
	getTotalLoadedProductsNumber(includePreviousNotLoadedPages?: boolean): number {
		if (!this.search.pages) {
			return 0;
		}

		const totalLoadedProducts: number = Object.values(this.search.pages).reduce((total: number, page: object[]) => (
			total + (page?.length || 0)), 0);

		return !includePreviousNotLoadedPages
			? totalLoadedProducts
			: ((this.getLowestLoadedPageNumber() - 1) * this.getPageSize()) + totalLoadedProducts;
	}

	/**
	 * Returns the lowest page number loaded till now.
	 * @return number
	 */
	getLowestLoadedPageNumber(): number {
		const pagesArray = this.getPages().map((p) => p.getPageNumber());

		if (pagesArray?.length) {
			return Math.min(...pagesArray);
		}

		return 0;
	}

	/**
	 * Returns the highest page number loaded till now.
	 * @return number
	 */
	getHighestLoadedPageNumber(): number {
		const pagesArray = this.getPages().map((p) => p.getPageNumber());

		if (pagesArray?.length) {
			return Math.max(...pagesArray);
		}

		return 0;
	}

	/**
	 * Returns the highest available page number in the current search.
	 * @return number
	 */
	getLastPageNumber(): number {
		return Math.ceil(this.getTotalProductsNumber() / this.getPageSize());
	}

	/**
	 * Returns the first previous not loaded page number.
	 * @return number|null
	 */
	getPrevPageNumberToLoad(): number | null {
		if (this.getLowestLoadedPageNumber() > 1) {
			return this.getLowestLoadedPageNumber() - 1;
		} else {
			return null;
		}
	}

	/**
	 * Returns true if all pages are loaded.
	 * @return boolean
	 */
	isFullyLoaded(): boolean {
		return this.getHighestLoadedPageNumber() >= this.getLastPageNumber();
	}

	/**
	 * Returns the first next not loaded page number.
	 * @return number|null
	 */
	getNextPageNumberToLoad(): number | null {
		if (!this.isFullyLoaded()) {
			return this.getHighestLoadedPageNumber() + 1;
		} else {
			return null;
		}
	}

	/**
	 * Returns the current query key.
	 * @return string
	 */
	getQueryKey(): string {
		return this.search.queryKey;
	}

	/**
	 * Returns the current search query.
	 * @return string
	 */
	getSearchQuery(): string {
		return this.search.query;
	}

	/**
	 * Returns an array of the productSearchHIts contained in the search result.
	 * @return ProductSearchHitModel[]
	 */
	getAllHits(): ProductSearchHitModel[] {
		return this.getPages().reduce((accumulator, pageModel) => [...accumulator, ...pageModel.getHits()], []);
	}

	/**
	 * Returns the total results number (contains also not loaded pages).
	 * @return number
	 */
	getTotalProductsNumber(): number | null {
		return this.search.total;
	}

	/**
	 * Returns the page size used for this search (parametric: from stored result or default).
	 * @return number
	 */
	getPageSize(): number | null {
		return this.pageSizeLimit ?? DEFAULT_SEARCH_PARAMS.limit;
	}

	/**
	 * Returns the selected refinements.
	 * @returns {Record<string, any>}
	 */
	getSelectedRefinements(): Record<string, any> {
		return this.search.selectedRefinements;
	}

	/**
	 * Returns the selected refinement for the given id.
	 * @param {string} id - selected refinement id
	 * @returns {string|null}
	 */
	getSelectedRefinementValue(id): string|null {
		return this.search.selectedRefinements &&
		(id in this.search.selectedRefinements)
			? this.search.selectedRefinements[id]
			: null;
	}

	/**
	 * Returns the selected refinements.
	 */
	getSelectedRefinementsValuesModels(): RefinementValueModel[] {
		let selectedRefinementValues = [];
		const selectedRefinements = this.getSelectedRefinements();

		for (const key in selectedRefinements) {
			const refinementModel = this.getRefinement(key);

			if (refinementModel) {
				const values = this.getSelectedRefinements()[key].split('|') || [];

				values.forEach((v) => {
					const rv = refinementModel.getRefinementValueModelByValue(v);

					if (rv) {
						selectedRefinementValues.push(refinementModel.getRefinementValueModelByValue(v));
					}
				});
			}
		}

		return selectedRefinementValues;
	}

	/**
	 * Returns selected refinement by id
	 * @param {string} refinementId - refinement id
	 * @returns {any} - selected refinement
	 */
	getSelectedRefinementById(refinementId) {
		const selectedRefinements = this.getSelectedRefinements();

		return selectedRefinements && selectedRefinements[refinementId];
	}

	/**
	 * Returns the available refinements rearranged according to FILTERS_ARRANGE_OPTIONS
	 * @param {boolean} includeHidden - if true also hidden ( disallowed and invalid ) refinements will be included
	 * @param {boolean} shouldRearrangeRefinments - if true rearranges refinements
	 * @returns {RefinementModel[]}
	 */
	getRefinements(includeHidden: boolean = false,
		shouldRearrangeRefinments: boolean = SHOULD_REARRANGE_REFINEMENTS
	): Array<RefinementModel> {
		if (!this.refinements) {
			const enableHideInPLPFilter = this.managerRepository
				.getConfigurationsMgr()
				.get('sitePreferences.enableHideInPLPFilter');

			const refinementsWithProducts = filterRefinementsWithProducts(
				this.search.refinements,
				this.getSelectedRefinements(),
				enableHideInPLPFilter,
			);

			const refinements = shouldRearrangeRefinments
				? rearrangeRefinements(refinementsWithProducts)
				: refinementsWithProducts;

			this.refinements =
				refinements.map((r) => new RefinementModel(this, r))
				|| [];
		}

		if (!includeHidden) {
			return this.refinements?.filter((r) =>
				!REFINEMENT_DISALLOW_LIST.includes(r?.getAttributeId())
				&& r.isValid()
			);
		} else {
			return this.refinements;
		}
	}

	/**
	 * Returns RefinementModel based on the given ID
	 * @param {string} id - the refinement ID
	 * @param {boolean} includeHidden - if true also hidden ( disallowed and invalid ) refinements will be included
	 * @returns {RefinementModel|null}
	 */
	getRefinement(id: string, includeHidden: boolean = false): RefinementModel|null {
		const findRefinement = (refinement: RefinementModel): RefinementModel|null => {
			if (refinement.getAttributeId() === id) {
				return refinement;
			} else if (refinement.getChildren(includeHidden)) {
				return findRefinements(refinement.getChildren(includeHidden));
			}
		};

		const findRefinements = (refinements: RefinementModel[]): RefinementModel|null => {
			for (const r of refinements) {
				const find = findRefinement(r);

				if (find) {
					return find;
				}
			}

			return null;
		};

		const foundRefinement = findRefinements(this.getRefinements(includeHidden));

		if (foundRefinement) {
			return foundRefinement;
		} else if (includeHidden && this.getSelectedRefinementValue(id)) {
			const value = this.getSelectedRefinementValue(id);

			return new RefinementModel(this, {
				attributeId: id,
				values: [{
					value: value,
					label: value,
					hitCount: 0
				}]
			});
		}

		return null;
	}

	/**
	 * Returns the selected sorting method.
	 * @return string
	 */
	getSelectedSortingOption(): string {
		return this.search.selectedSortingOption;
	}

	/**
	 * Returns the available sorting methods.
	 * @return string[]
	 */
	getSortingOptions(): ProductSearchSortingOption[] {
		return this.search.sortingOptions || [];
	}

	/**
	 * Returns grid size
	 * @returns number
	 */
	getGridSize(): number {
		return this.search.gridSize;
	}

	/**
	 * Returns search redirect url
	 * @returns string
	 */
	getSearchRedirectUrl(): string {
		if (this.search?.redirectUrl) {
			this.redirectUrl = this.search.redirectUrl;
		}

		if (this.redirectUrl === undefined) {
			if (this.search.searchPhraseSuggestions?.c_searchRedirectUrl) {
				this.redirectUrl = isSfccURL(this.search.searchPhraseSuggestions.c_searchRedirectUrl)
					? this.managerRepository.getLocalesMgr().updateLinkWithLocale(
						this.search.searchPhraseSuggestions.c_searchRedirectUrl
					)
					: this.search.searchPhraseSuggestions.c_searchRedirectUrl;
			} else {
				this.redirectUrl = null;
			}
		}

		return this.redirectUrl;
	}

	/**
	 * Returns true if the last page is loaded
	 * @returns {boolean}
	 */
	isLastPageLoaded(): boolean {
		return this.getHighestLoadedPageNumber() === this.getLastPageNumber();
	}

	/**
	 * Returns the current SearchMgr instance
	 * @returns {AbstractSearchProvider}
	 */
	getSearchProvider(): AbstractSearchProvider {
		return this.searchProvider;
	}

	/**
	 * Returns the query id come from provider request
	 * @return {string| undefined}
	 */
	getQueryId(): string | undefined {
		return this.search.queryId;
	}

	/**
	 * Returns saved searches
	 * @param {string} storeKey - store key
	 * @returns {Array<string>} savedSearches - the saved searches in local storage
	 */
	getRecentSearches(storeKey: string): {value: string, url: string}[] {
		let savedSearches = [];

		try {
			savedSearches = JSON.parse(localStorage.getItem(storeKey)) || [];
		} catch (e) {
			console.error('🔥 Error parsing saved searches from local storage', e);
		}

		return savedSearches;
	}

	/**
	 * Removes a recent search term from the saved searches.
	 * @param {string} storeKey - The key used to store the searches in local storage.
	 * @param {string} term - The search term to remove.
	 * @returns {Array<string>} updatedHistory - the updated saved searches
	 */
	removeTermInRecentSearch(storeKey: string, term: {value: string, url: string}): {value: string, url: string}[] {
		const updatedHistory = this.getRecentSearches(storeKey).filter((item) => item.value !== term.value);
		localStorage.setItem(storeKey, JSON.stringify(updatedHistory));

		return updatedHistory;
	}

	/**
	 * Returns the url applicable selected refinements.
	 * @returns {Record<string, any>}
	 */
	getUrlApplicableSelectedRefinements() {
		return this.search.urlApplicableSelectedRefinements;
	}
}

/**
 * Returns refinements filtered by hitCount, or if they are among the active ones
 * @param {refinements} searchRefinements - refinements as received from the api
 * @param {refinements} selectedSearchRefinements - selected refinements as received from the api
 * @param {boolean} shouldIgnoreHitCountWithoutFilters - whether to preserve zero-hit values without user filters
 * @returns {refinements} - the api refinements filtered
 */
const filterRefinementsWithProducts = (
	searchRefinements,
	selectedSearchRefinements,
	shouldIgnoreHitCountWithoutFilters,
) => {
	const hasUserSelectedRefinements = Object.keys(selectedSearchRefinements || {})
		.some((attributeId) =>
			!REFINEMENT_DISALLOW_LIST.includes(attributeId)
			&& !!selectedSearchRefinements[attributeId]?.length
		);

	const shouldPreserveRefinementValues = !!selectedSearchRefinements?.cgid
		&& !hasUserSelectedRefinements
		&& shouldIgnoreHitCountWithoutFilters;

	return searchRefinements?.map((refinement) => {
		const attributeId = refinement?.attributeId;
		const selectedRefinements = selectedSearchRefinements
			&& selectedSearchRefinements[attributeId]
			? selectedSearchRefinements[attributeId]
				.split(REFINEMENTS_VALUE_SEPARATOR) : [];
		const refinementValuesWithProducts = refinement?.values?.filter((refinementValue) =>  {
			return shouldPreserveRefinementValues
				|| refinementValue?.hitCount > 0
				|| selectedRefinements?.includes(refinementValue?.value);
		});

		return {...refinement, values: refinementValuesWithProducts};
	});
};

/**
 * Returns refinements ordered with custom hierarchy as indicated by arg parameters, listed in FILTERS_ARRANGE_OPTIONS.
 * @param {refinements} refinements - refinements as received from the api
 * @returns {refinements} refinementsRearranged - rearranged
 */
const rearrangeRefinements = (refinements: Array<ProductSearchRefinement> = []): Array<ProductSearchRefinement> => {
	const refinementsRearranged: Array<RearrangedProductSearchRefinement> = Array.from(refinements);

	for (let arg of FILTERS_ARRANGE_OPTIONS) {
		let children = [];

		for (let value of arg.refinementsIds) {
			const indexFountAt: number = refinements.findIndex((row) => row.attributeId === value);

			if (indexFountAt !== -1) {
				refinementsRearranged.splice(indexFountAt, 1);

				if (refinements[indexFountAt]?.values) {
					children.push(refinements[indexFountAt]);
				}
			}
		}

		let newRefinement = {
			attributeId: arg.attributeId.toLowerCase(),
			label: arg.attributeId,
			values: children,
			children
		};

		refinementsRearranged.splice(arg.position, 0, newRefinement);
	}

	const index = refinementsRearranged.findIndex((el) => el.attributeId === CATEGORIES_FILTER_ID);

	if (index > -1) {
		let tree = [];
		const categoryFilter = refinementsRearranged[index];

		if (categoryFilter?.values) {
			for (let value of categoryFilter?.values) {
				if (value.value) {
					const br = value?.value?.split(' > ');

					if (br.length > 1) {
						tree.push(br);
					}
				}
			}
		}

		let root = {
			values: [],
			children: []
		};

		for (let branch of tree) {
			rearrangeCategoryFilter(categoryFilter, root, branch, 0);
		}

		let newRefinement = {
			attributeId: categoryFilter.attributeId,
			label: categoryFilter.label,
			values: root.values,
			children: root.children
		};

		refinementsRearranged.splice(index, 1, newRefinement);
	}

	return refinementsRearranged;
};

/**
 * Returns category refinements custom hierarchy as indicated by categoryFilter.values.
 * @param {ProductSearchRefinement} categoryFilter - category refinements as received from the api
 * @returns {Object} newCategoryHierarchy - the new category hierarchy
 */
const rearrangeCategoryFilter = (categoryFilter, categoryFilterRoot, path, level) => {
	if (!path || path.length < level + 1) {
		return categoryFilterRoot;
	}

	if (categoryFilterRoot.values && !categoryFilterRoot.values.find(child => child.label === path[level])) {
		const pathValue = path.join(' > ');
		const child = categoryFilter.values.find((cf) => cf.label === pathValue);
		const newChild = {
			attributeId: categoryFilter.attributeId,
			presentationId: child.presentationId,
			hitCount: child.hitCount,
			description: child.description,
			label: path[level],
			value: path[level] === path[path.length - 1] ? pathValue : '',
			values: [],
			children: []
		};

		if (newChild.description === 'ALL') {
			categoryFilterRoot.values.unshift(newChild);
			categoryFilterRoot.children.unshift(newChild);
		} else {
			categoryFilterRoot.values.push(newChild);
			categoryFilterRoot.children.push(newChild);
		}

		return rearrangeCategoryFilter(categoryFilter, newChild, path, level + 1);
	}

	const presentValue = categoryFilterRoot.values?.find(child => child.label === path[level]);

	if (!presentValue || !presentValue.hasOwnProperty('hitCount')) {
		return categoryFilterRoot;
	}

	return rearrangeCategoryFilter(categoryFilter, presentValue, path, level + 1);
};

export default SearchModel;
